import { Injectable, NgZone, ElementRef, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { USDLoader } from 'three/examples/jsm/loaders/USDLoader.js';
import { TelemetryFrame, XrSession, SpatialTarget, SceneObject, SceneSnapshot, PerformanceFrame } from '../../../models/session.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { SessionDataService } from './session-data.service';


@Injectable()
export class SessionEngineService implements OnDestroy {
  private dataService = inject(SessionDataService);

  // THREE Core
  scene!: THREE.Scene;
  camera!: THREE.PerspectiveCamera;
  renderer!: THREE.WebGLRenderer;
  controls!: OrbitControls;
  sceneGroup = new THREE.Group();
  gazeTrailGroup = new THREE.Group();
  raycaster = new THREE.Raycaster();
  mouse = new THREE.Vector2();

  // Entities
  headMesh!: THREE.Mesh;
  leftHandMesh!: THREE.Mesh;
  rightHandMesh!: THREE.Mesh;
  gazeHitDotGeom = new THREE.SphereGeometry(0.01, 8, 8);
  gazeHitMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });

  // State
  showScene = true;
  showGazeTrail = true;
  playing = false;
  currentTime = 0;
  duration = 0;
  progress = 0;
  currentAbsoluteTime = 0;
  
  // Data References
  session: XrSession | null = null;
  telemetryFrames: TelemetryFrame[] = [];
  platformId: Object = 'browser';
  
  // Internal
  private animationFrameId = 0;
  private lastTimestamp = 0;
  public isLive = false;
  private destroyed = false;
  
  // UI Reactive State
  public sceneReady$ = new BehaviorSubject<boolean>(false);

  highlightObject(name: string | null) {
    if (!this.sceneGroup) return;
    if (!name) {
      // restore all
      this.sceneGroup.children.forEach((child: any) => {
        child.traverse((mesh: any) => {
          if (mesh instanceof THREE.Mesh || mesh instanceof THREE.LineSegments) {
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            mats.forEach((mat: THREE.Material) => {
              if (mat) {
                if (mat.userData['originalOpacity'] !== undefined) {
                  mat.opacity = mat.userData['originalOpacity'];
                }
                if (mat.userData['originalDepthWrite'] !== undefined) {
                  mat.depthWrite = mat.userData['originalDepthWrite'];
                }
              }
            });
          }
        });
      });
      return;
    }

    this.sceneGroup.children.forEach((child: any) => {
      const isSelected = child.name === name;
      child.traverse((mesh: any) => {
        if (mesh instanceof THREE.Mesh || mesh instanceof THREE.LineSegments) {
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          mats.forEach((mat: THREE.Material) => {
            if (mat) {
              if (mat.userData['originalOpacity'] === undefined) {
                mat.userData['originalOpacity'] = mat.opacity;
              }
              if (mat.userData['originalDepthWrite'] === undefined) {
                mat.userData['originalDepthWrite'] = mat.depthWrite;
              }

              // Apply highlight or dim
              // Use the original opacity for the selected object so it doesn't become artificially intense
              mat.opacity = isSelected ? mat.userData['originalOpacity'] : 0.05;
              mat.depthWrite = false;
            }
          });
        }
      });
    });
  }
  public pendingModelDownloads$ = new BehaviorSubject<number>(0);
  public frameApplied$ = new Subject<number>(); // To notify component to update active breadcrumbs etc.
  selectedObjectNombre: string | null = null;
  editModeEnabled = false;
  debugRotX = 0;
  debugRotY = 0;
  debugRotZ = 0;
  debugPosX = 0;
  debugPosY = 0;
  debugPosZ = 0;
  savingRotation = false;
  
  // Dependencies injected manually via init() since we don't have TelemetryService injected here
  private telemetryService: any = null;

  public refreshObjectTransform(name: string | null) {
    if (!name) return;
    const child = this.sceneGroup.children.find(c => c.name === name);
    if (!child) return;
    const sceneObj = this.session?.sceneSnapshot?.objects?.find((o: any) => o.name === name);
    if (!sceneObj) return;

    let posX = sceneObj.posX, posY = sceneObj.posY, posZ = sceneObj.posZ;
    let rotX = sceneObj.rotX, rotY = sceneObj.rotY, rotZ = sceneObj.rotZ, rotW = sceneObj.rotW;

    if (child.userData['isDynamic']) {
      const frame = this.telemetryFrames[this.currentPerformanceIndex || 0];
      if (frame?.dynamicObjects && frame.dynamicObjects[name]) {
        const pose = frame.dynamicObjects[name];
        posX = pose.x; posY = pose.y; posZ = pose.z;
        rotX = pose.qx; rotY = pose.qy; rotZ = pose.qz; rotW = pose.qw;
      }
    }

    child.position.set(posX, posY, -posZ);
    const baseQuat = new THREE.Quaternion(-rotX, -rotY, rotZ, rotW);

    const isEditingThisObject = this.editModeEnabled && this.selectedObjectNombre === name;
    
    if (isEditingThisObject) {
      const offsetEuler = new THREE.Euler(
        this.debugRotX * Math.PI / 180,
        this.debugRotY * Math.PI / 180,
        this.debugRotZ * Math.PI / 180,
        'XYZ'
      );
      const offsetQuat = new THREE.Quaternion().setFromEuler(offsetEuler);
      baseQuat.multiply(offsetQuat);
    } else if (sceneObj?.rotationOffset) {
      const offsetEuler = new THREE.Euler(
        sceneObj.rotationOffset.x * Math.PI / 180,
        sceneObj.rotationOffset.y * Math.PI / 180,
        sceneObj.rotationOffset.z * Math.PI / 180,
        'XYZ'
      );
      const offsetQuat = new THREE.Quaternion().setFromEuler(offsetEuler);
      baseQuat.multiply(offsetQuat);
    }
    child.quaternion.copy(baseQuat);

    if (isEditingThisObject) {
      const posOffsetVec = new THREE.Vector3(this.debugPosX, this.debugPosY, this.debugPosZ);
      posOffsetVec.applyQuaternion(child.quaternion);
      child.position.add(posOffsetVec);
    } else if (sceneObj?.positionOffset) {
      const posOffsetVec = new THREE.Vector3(sceneObj.positionOffset.x, sceneObj.positionOffset.y, sceneObj.positionOffset.z);
      posOffsetVec.applyQuaternion(child.quaternion);
      child.position.add(posOffsetVec);
    }
  }

  public toggleEditMode() {
    if (this.playing) return;
    this.editModeEnabled = !this.editModeEnabled;
    this.refreshObjectTransform(this.selectedObjectNombre);
  }

  public saveDebugRotation(telemetryService: any) {
    if (!this.selectedObjectNombre || !this.session?.id) return;
    
    this.savingRotation = true;
    const rotOffset = { x: this.debugRotX, y: this.debugRotY, z: this.debugRotZ };
    const posOffset = { x: this.debugPosX, y: this.debugPosY, z: this.debugPosZ };
    
    telemetryService.updateModelTransform(this.session.id, this.selectedObjectNombre, rotOffset, posOffset).subscribe({
      next: (res: any) => {
        this.savingRotation = false;
        this.editModeEnabled = false;
        
        // Update local session state
        if (this.session?.sceneSnapshot?.objects) {
          const obj = this.session.sceneSnapshot.objects.find((o: any) => o.name === this.selectedObjectNombre);
          if (obj) {
            obj.rotationOffset = rotOffset;
            obj.positionOffset = posOffset;
          }
        }
        
        // Return to normal playback mode with the new offset
        this.refreshObjectTransform(this.selectedObjectNombre);
        this.findAndApplyFrame();
      },
      error: (err: any) => {
        console.error('Error saving rotation:', err);
        this.savingRotation = false;
        alert('Error al guardar la rotación.');
      }
    });
  }

  public resetDebugRotation() {
    this.debugRotX = 0;
    this.debugRotY = 0;
    this.debugRotZ = 0;
    this.debugPosX = 0;
    this.debugPosY = 0;
    this.debugPosZ = 0;
    this.refreshObjectTransform(this.selectedObjectNombre);
  }

  public onTransformChange(t: {rotX: number, rotY: number, rotZ: number, posX: number, posY: number, posZ: number}) {
    this.debugRotX = t.rotX; this.debugRotY = t.rotY; this.debugRotZ = t.rotZ;
    this.debugPosX = t.posX; this.debugPosY = t.posY; this.debugPosZ = t.posZ;
    this.refreshObjectTransform(this.selectedObjectNombre);
  }
  isLoadingTelemetry = false;
  gazeDotsQueue: any[] = [];
  currentFrameIdx = 0;
  filteredBreadcrumbs: any[] = [];
  performanceFrames: any[] = [];
  currentPerformanceIndex = 0;
  currentPerformanceFrame: any = null;
  
  get averageFps(): number {
    if (this.session?.performanceCount) {
      return (this.session.sumFps || 0) / this.session.performanceCount;
    }
    return this.session?.performance_snapshot?.real_fps || 0;
  }

  get refreshRate(): number {
    return this.currentPerformanceFrame?.refreshRate || this.session?.performance_snapshot?.display_refresh_rate || 90;
  }

  get currentCpuLevel(): number {
    return this.currentPerformanceFrame?.cpuLevel || 0;
  }

  get currentCpuCycleTime(): number | null {
    return this.currentPerformanceFrame?.cpuCycleTimeMs ?? this.session?.performance_snapshot?.cpuCycleTimeMs ?? null;
  }

  get currentGpuLevel(): number {
    return this.currentPerformanceFrame?.gpuLevel || 0;
  }

  get currentGpuCycleTime(): number | null {
    return this.currentPerformanceFrame?.gpuCycleTimeMs ?? this.session?.performance_snapshot?.gpuCycleTimeMs ?? null;
  }

  // We need a cdr to trigger updates in the parent component
  private cdr: ChangeDetectorRef | null = null;

  rendererContainer?: ElementRef;

  constructor(private ngZone: NgZone) {}


  public init(
    container: ElementRef, 
    session: XrSession, 
    telemetryFrames: TelemetryFrame[],
    cdr: ChangeDetectorRef,
    platformId: Object,
    isLive: boolean
  ) {
    this.rendererContainer = container;
    this.session = session;
    this.telemetryFrames = telemetryFrames;
    this.cdr = cdr;
    this.platformId = platformId;
    this.isLive = isLive;
    
    if (this.telemetryFrames.length > 0) {
      const first = this.telemetryFrames[0].timestampOffsetMs;
      const last = this.telemetryFrames[this.telemetryFrames.length - 1].timestampOffsetMs;
      this.duration = (last - first) / 1000;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.ngZone.runOutsideAngular(() => {
        this.initThreeJS();
      });
    }
  }

  public updateTelemetry(frames: TelemetryFrame[]) {
    this.telemetryFrames = frames;
    if (this.telemetryFrames.length > 0) {
      const first = this.telemetryFrames[0].timestampOffsetMs;
      const last = this.telemetryFrames[this.telemetryFrames.length - 1].timestampOffsetMs;
      this.duration = (last - first) / 1000;
    }
  }

  public updateSession(session: XrSession) {
    this.session = session;
  }

  public updatePerformance(frames: PerformanceFrame[]) {
    this.performanceFrames = frames;
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
    this.cdr = null;
  }

  initThreeJS() {
    if (!this.rendererContainer) return;
    const container = this.rendererContainer.nativeElement;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0a0a);
    this.scene.fog = new THREE.Fog(0x0a0a0a, 10, 50);

    const grid = new THREE.GridHelper(20, 20, 0x3b82f6, 0x1e293b);
    this.scene.add(grid);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    this.scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 5);
    this.scene.add(directionalLight);

    // Add scale references
    const gridHelper = new THREE.GridHelper(10, 10, 0x888888, 0x444444);
    gridHelper.position.y = -0.01; // Slightly below zero to prevent z-fighting with floor objects
    this.scene.add(gridHelper);

    const axesHelper = new THREE.AxesHelper(1);
    this.scene.add(axesHelper);

    this.camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 100);
    this.camera.position.set(0, 2, 4);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.target.set(0, 1.5, 0);
    this.controls.update();

    window.addEventListener('resize', this.onWindowResize.bind(this));
    this.createDummyModels();

    // Add scene and gaze groups
    this.scene.add(this.sceneGroup);
    this.scene.add(this.gazeTrailGroup);

    if (this.telemetryFrames.length > 0) {
      this.applyFrame(0);
    }

    this.animate();
  }

  createDummyModels() {
    const headGeom = new THREE.BoxGeometry(0.2, 0.1, 0.1);
    const headMat = new THREE.MeshStandardMaterial({ color: 0x3b82f6, roughness: 0.2, metalness: 0.8 });
    this.headMesh = new THREE.Mesh(headGeom, headMat);
    this.scene.add(this.headMesh);

    const handGeom = new THREE.BoxGeometry(0.05, 0.1, 0.15);
    const handMat = new THREE.MeshStandardMaterial({ color: 0x60a5fa, roughness: 0.5 });
    this.leftHandMesh = new THREE.Mesh(handGeom, handMat);
    this.scene.add(this.leftHandMesh);

    this.rightHandMesh = new THREE.Mesh(handGeom, handMat);
    this.scene.add(this.rightHandMesh);

    const gazeMat = new THREE.LineBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0.5 });
    const gazeGeom = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -5)]);
    const gazeLine = new THREE.Line(gazeGeom, gazeMat);
    this.headMesh.add(gazeLine);
  }

  onWindowResize() {
    if (!this.camera || !this.renderer) return;
    if (!this.rendererContainer) return;
    const container = this.rendererContainer.nativeElement;
    this.camera.aspect = container.clientWidth / container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(container.clientWidth, container.clientHeight);
  }

  animate() {
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
    const now = performance.now();
    if (this.playing) {
      const dt = (now - this.lastTimestamp) / 1000;
      this.currentTime += dt;
      if (this.currentTime >= this.duration) {
        this.currentTime = this.duration;
        if (!this.isLoadingTelemetry && !this.isLive) {
          this.playing = false;
        }
      }
      this.updateProgress();
      this.findAndApplyFrame();
      if(this.cdr) this.cdr.detectChanges();
    }
    this.lastTimestamp = now;
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  clearGazeTrail() {
    this.gazeDotsQueue.forEach(dot => {
      if (dot.parent) dot.parent.remove(dot);
    });
    this.gazeDotsQueue = [];
    while (this.gazeTrailGroup.children.length > 0) {
      this.gazeTrailGroup.remove(this.gazeTrailGroup.children[0]);
    }
  }

  togglePlay() {
    this.playing = !this.playing;
    if (this.playing && this.currentTime >= this.duration) {
      this.currentTime = 0;
      this.clearGazeTrail();
    }
    this.lastTimestamp = performance.now();
  }

  toggleSceneVisibility() {
    this.showScene = !this.showScene;
    if (this.sceneGroup) this.sceneGroup.visible = this.showScene;
  }

  toggleGazeTrail() {
    this.showGazeTrail = !this.showGazeTrail;
    if (!this.selectedObjectNombre) {
      if (this.gazeTrailGroup) this.gazeTrailGroup.visible = this.showGazeTrail;
      this.gazeDotsQueue.forEach(dot => dot.visible = this.showGazeTrail);
    }
  }

  seek(percent: number) {
    this.currentTime = percent * this.duration;
    this.updateProgress();
    this.clearGazeTrail();
    
    // Find target index to rebuild up to
    if (this.telemetryFrames.length > 0) {
      const firstTs = this.telemetryFrames[0].timestampOffsetMs;
      const targetTs = firstTs + (this.currentTime * 1000);
      let bestIdx = 0;
      for (let i = 0; i < this.telemetryFrames.length; i++) {
        if (this.telemetryFrames[i].timestampOffsetMs <= targetTs) {
          bestIdx = i;
        } else {
          break;
        }
      }
      this.rebuildGazeTrail(bestIdx);
    }
    
    this.findAndApplyFrame();
  }

  rebuildGazeTrail(targetIdx: number) {
    const startIdx = Math.max(0, targetIdx - 1000);
    // Loop up to targetIdx - 1 to leave the final frame for findAndApplyFrame()
    for (let i = startIdx; i < targetIdx; i++) {
       if (this.telemetryFrames[i]) {
           this.applyFrame(i, true);
       }
    }
  }

  updateProgress() {
    this.progress = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
  }

  findAndApplyFrame() {
    if (this.telemetryFrames.length === 0) return;
    const firstTs = this.telemetryFrames[0].timestampOffsetMs;
    const targetTs = firstTs + (this.currentTime * 1000);
    let bestIdx = 0;
    for (let i = 0; i < this.telemetryFrames.length; i++) {
      if (this.telemetryFrames[i].timestampOffsetMs <= targetTs) {
        bestIdx = i;
      } else {
        break;
      }
    }
    if (bestIdx !== this.currentFrameIdx) {
      this.applyFrame(bestIdx);
      this.currentFrameIdx = bestIdx;
    }

    if (this.session) {
      // The absolute most precise way to find the session start time for existing sessions
      // is to look at the very first Breadcrumb ever recorded (which usually happens at T=0).
      let sessionStartTimeMs = this.session.timestamp || 0;

      if (this.session.breadcrumbs && this.session.breadcrumbs.length > 0) {
        sessionStartTimeMs = this.session.breadcrumbs[0].timestamp;
      } else if (this.isLive) {
        sessionStartTimeMs = (this.session.timestamp || Date.now()) - (this.duration * 1000);
      }

      this.currentAbsoluteTime = sessionStartTimeMs + targetTs;
    }

    if (this.performanceFrames.length > 0) {
      const prevIdx = this.currentPerformanceIndex;
      let pIdx = 0;
      for (let i = 0; i < this.performanceFrames.length; i++) {
        if (this.performanceFrames[i].timestampOffsetMs <= targetTs) {
          pIdx = i;
        } else {
          break;
        }
      }
      if (pIdx !== prevIdx) {
        this.currentPerformanceIndex = pIdx;
        this.currentPerformanceFrame = this.performanceFrames[pIdx];
      }
    }
  }

  applyFrame(index: number, skipRaycast: boolean = false) {
    const frame = this.telemetryFrames[index];
    if (!frame) return;
    if (frame.head) {
      this.headMesh.position.set(frame.head.x, frame.head.y, -frame.head.z);
      this.headMesh.quaternion.set(-frame.head.qx, -frame.head.qy, frame.head.qz, frame.head.qw);
      if (frame.head.sx !== undefined) this.headMesh.scale.set(frame.head.sx, frame.head.sy, frame.head.sz);
    }
    if (frame.leftHand) {
      this.leftHandMesh.position.set(frame.leftHand.x, frame.leftHand.y, -frame.leftHand.z);
      this.leftHandMesh.quaternion.set(-frame.leftHand.qx, -frame.leftHand.qy, frame.leftHand.qz, frame.leftHand.qw);
      if (frame.leftHand.sx !== undefined) this.leftHandMesh.scale.set(frame.leftHand.sx, frame.leftHand.sy, frame.leftHand.sz);
    }
    if (frame.rightHand) {
      this.rightHandMesh.position.set(frame.rightHand.x, frame.rightHand.y, -frame.rightHand.z);
      this.rightHandMesh.quaternion.set(-frame.rightHand.qx, -frame.rightHand.qy, frame.rightHand.qz, frame.rightHand.qw);
      if (frame.rightHand.sx !== undefined) this.rightHandMesh.scale.set(frame.rightHand.sx, frame.rightHand.sy, frame.rightHand.sz);
    }
    // Update dynamic objects FIRST so their matrixWorld is up to date for gaze trail hits
    // Also toggle visibility for dynamic objects
    const currentMs = frame.timestampOffsetMs;
    this.sceneGroup.children.forEach((child: any) => {
      let shouldBeVisible = true;
      const spawnTimeMs = child.userData['spawnTimeMs'] || 0;

      // ALL objects (static or dynamic) must be hidden before their spawn time.
      // Add a 500ms tolerance to prevent flashing at the exact moment of creation.
      if (currentMs < (spawnTimeMs - 500)) {
        shouldBeVisible = false;
      }

      if (child.userData['isDynamic']) {
        const firstFrame = this.dataService.dynamicObjectFirstFrame[child.name];
        
        // If it DOES appear in telemetry, use the more precise telemetry delta first frame.
        if (firstFrame !== undefined && index < firstFrame) {
          shouldBeVisible = false;
        }
      }

      child.visible = shouldBeVisible;

      if (child.userData['isDynamic']) {
        if (frame.dynamicObjects && frame.dynamicObjects[child.name]) {
          child.visible = true;
          const pose = frame.dynamicObjects[child.name];
          child.position.set(pose.x, pose.y, -pose.z);
          
          const baseQuat = new THREE.Quaternion(-pose.qx, -pose.qy, pose.qz, pose.qw);
          const sceneObj = this.session?.sceneSnapshot?.objects?.find((o: any) => o.name === child.name);
          const isEditingThisObject = this.editModeEnabled && this.selectedObjectNombre === child.name;
          
          if (isEditingThisObject) {
            const offsetEuler = new THREE.Euler(
              this.debugRotX * Math.PI / 180,
              this.debugRotY * Math.PI / 180,
              this.debugRotZ * Math.PI / 180,
              'XYZ'
            );
            const offsetQuat = new THREE.Quaternion().setFromEuler(offsetEuler);
            baseQuat.multiply(offsetQuat);
          } else if (sceneObj?.rotationOffset) {
            const offsetEuler = new THREE.Euler(
              sceneObj.rotationOffset.x * Math.PI / 180,
              sceneObj.rotationOffset.y * Math.PI / 180,
              sceneObj.rotationOffset.z * Math.PI / 180,
              'XYZ'
            );
            const offsetQuat = new THREE.Quaternion().setFromEuler(offsetEuler);
            baseQuat.multiply(offsetQuat);
          }
          child.quaternion.copy(baseQuat);

          if (isEditingThisObject) {
            const posOffsetVec = new THREE.Vector3(this.debugPosX, this.debugPosY, this.debugPosZ);
            posOffsetVec.applyQuaternion(child.quaternion);
            child.position.add(posOffsetVec);
          } else if (sceneObj?.positionOffset) {
            const posOffsetVec = new THREE.Vector3(sceneObj.positionOffset.x, sceneObj.positionOffset.y, sceneObj.positionOffset.z);
            posOffsetVec.applyQuaternion(child.quaternion);
            child.position.add(posOffsetVec);
          }

          if (pose.sx !== undefined) {
             const isUI = child.name.toLowerCase().includes('canvas') || child.name.toLowerCase().includes('text') || child.name.toLowerCase().includes('panel');
             if (!isUI || pose.sx > 0.05) {
               child.scale.set(pose.sx, pose.sy, pose.sz);
             }
          }
        }
        // We do NOT set child.visible = false here because stationary dynamic objects might not send poses every frame
      }
    });

    // Ensure world matrices are up to date for local space conversions
    this.sceneGroup.updateMatrixWorld(true);

    // Add gaze hit dot
    if (frame.gazeHitPoint && !this.selectedObjectNombre) {
      this.addGazeHitDot(frame, skipRaycast);
    }
  }

  addGazeHitDot(frame: TelemetryFrame, skipRaycast: boolean = false) {
    if (!frame.gazeHitPoint) return;

    if (this.gazeDotsQueue.length > 2000) {
      const oldDot = this.gazeDotsQueue.shift();
      if (oldDot && oldDot.parent) {
        oldDot.parent.remove(oldDot);
      }
    }

    const dot = new THREE.Mesh(this.gazeHitDotGeom, this.gazeHitMaterial);
    dot.name = 'gazeDot';
    dot.raycast = () => { }; // Disable raycasting to prevent blocking
    const hitWorld = new THREE.Vector3(frame.gazeHitPoint.x, frame.gazeHitPoint.y, -frame.gazeHitPoint.z);

    if (frame.gazeTarget) {
      const targetChild = this.sceneGroup.children.find(c => c.name === frame.gazeTarget);
      if (targetChild) {
        let hitLocal = hitWorld.clone().applyMatrix4(targetChild.matrixWorld.clone().invert());

        // Universal surface snapping: raycast from outside towards the center to find the exact visual surface
        if (!skipRaycast) {
          let directionLocal = hitLocal.clone();
          if (directionLocal.lengthSq() > 0.00001) {
            directionLocal.normalize();
            const rayOriginLocal = directionLocal.clone().multiplyScalar(10); // Start far outside

            // Transform local ray to targetChild's current world space
            const rayOriginWorld = rayOriginLocal.clone().applyMatrix4(targetChild.matrixWorld);
            const zeroWorld = new THREE.Vector3(0, 0, 0).applyMatrix4(targetChild.matrixWorld);
            const rayDirWorld = zeroWorld.clone().sub(rayOriginWorld).normalize();

            const worldRaycaster = new THREE.Raycaster(rayOriginWorld, rayDirWorld);
            const intersects = worldRaycaster.intersectObject(targetChild, true);

            const validHit = intersects.find(i => i.object.name !== 'heatmapDot' && i.object.name !== 'gazeDot' && i.object.type === 'Mesh');
            if (validHit) {
              hitLocal = targetChild.worldToLocal(validHit.point.clone()); // perfectly snapped to the geometry surface
            }
          }
        }

        dot.position.copy(hitLocal);
        dot.scale.set(1 / targetChild.scale.x, 1 / targetChild.scale.y, 1 / targetChild.scale.z);
        dot.visible = this.showGazeTrail;
        targetChild.add(dot);
        this.gazeDotsQueue.push(dot);
        return;
      }
    }

    dot.position.copy(hitWorld);
    dot.visible = this.showGazeTrail;
    this.gazeTrailGroup.add(dot);
    this.gazeDotsQueue.push(dot);
  }

  renderSceneSnapshot(snapshot: SceneSnapshot) {
    // Clear previous scene objects
    while (this.sceneGroup.children.length > 0) {
      this.sceneGroup.remove(this.sceneGroup.children[0]);
    }

    for (const obj of snapshot.objects) {
      let geom: THREE.BufferGeometry;

      let sx = Math.max(0.001, obj.scaleX);
      let sy = Math.max(0.001, obj.scaleY);
      let sz = Math.max(0.001, obj.scaleZ);

      const bounds = obj.meshBounds || obj.colliderBounds;

      if (obj.shape === 'UI' || obj.shape === 'Quad' || obj.name.toLowerCase().includes('canvas') || obj.name.toLowerCase().includes('text') || obj.name.toLowerCase().includes('panel')) {
        if (bounds && bounds.sizeX > 0) {
          sx = bounds.sizeX;
          sy = bounds.sizeY;
          sz = bounds.sizeZ > 0.001 ? bounds.sizeZ : 0.01;
        } else if (sx < 0.05 && sy < 0.05) {
          sx = 0.5;
          sy = 0.5;
          sz = 0.01;
        }
      }

      // Construct shape-based geometry
      switch (obj.shape) {
        case 'Sphere':
          geom = new THREE.SphereGeometry(0.5, 16, 16);
          break;
        case 'Capsule':
          geom = new THREE.CapsuleGeometry(0.5, 1, 4, 8);
          break;
        case 'Cylinder':
          geom = new THREE.CylinderGeometry(0.5, 0.5, 2, 16);
          break;
        case 'Plane':
          geom = new THREE.PlaneGeometry(10, 10);
          geom.rotateX(-Math.PI / 2); // Unity plane is horizontal
          break;
        case 'Quad':
        case 'UI':
          geom = new THREE.PlaneGeometry(1, 1);
          break;
        case 'Box':
        default:
          geom = new THREE.BoxGeometry(1, 1, 1);
          break;
      }

      // Interactable = green wireframe, others = blue-gray
      const color = obj.isInteractable ? 0x22c55e : 0x475569;
      const opacity = obj.isInteractable ? 0.25 : 0.08;

      // Solid transparent fill
      const solidMat = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: opacity,
        depthWrite: false,
        side: THREE.DoubleSide
      });
      const solidMesh = new THREE.Mesh(geom, solidMat);

      // Wireframe edges
      const edges = new THREE.EdgesGeometry(geom);
      const lineMat = new THREE.LineBasicMaterial({
        color: color,
        transparent: true,
        opacity: obj.isInteractable ? 0.8 : 0.35
      });
      const wireframe = new THREE.LineSegments(edges, lineMat);

      const group = new THREE.Group();
      group.name = obj.name;
      group.userData = {
        name: obj.name,
        tag: obj.tag,
        layer: obj.layer,
        isInteractable: obj.isInteractable,
        isDynamic: obj.isDynamic,
        modelUrl: obj.modelUrl
      };

      // Apply actual Unity Transform to the group
      group.position.set(obj.posX, obj.posY, -obj.posZ);
      const baseQuat = new THREE.Quaternion(-obj.rotX, -obj.rotY, obj.rotZ, obj.rotW);
      
      // Look up offset in session metadata
      const sceneObj = this.session?.sceneSnapshot?.objects?.find((o: any) => o.name === obj.name);
      
      // Only apply offset if we are NOT in edit mode for this specific object
      const isEditingThisObject = this.editModeEnabled && this.selectedObjectNombre === obj.name;
      
      if (sceneObj?.rotationOffset && !isEditingThisObject) {
        const offsetEuler = new THREE.Euler(
          sceneObj.rotationOffset.x * Math.PI / 180,
          sceneObj.rotationOffset.y * Math.PI / 180,
          sceneObj.rotationOffset.z * Math.PI / 180,
          'XYZ'
        );
        const offsetQuat = new THREE.Quaternion().setFromEuler(offsetEuler);
        baseQuat.multiply(offsetQuat);
      }
      
      group.quaternion.copy(baseQuat);
      group.scale.set(sx, sy, sz);

      if (sceneObj?.positionOffset && !isEditingThisObject) {
        // positionOffset is applied in local space just like how rotation offset works via wrapper child
        // Wait, if it's applied to the child (fbxRoot), it gets rotated by group.quaternion!
        // So we just add positionOffset * group.quaternion to group.position
        const posOffsetVec = new THREE.Vector3(sceneObj.positionOffset.x, sceneObj.positionOffset.y, sceneObj.positionOffset.z);
        posOffsetVec.applyQuaternion(group.quaternion);
        group.position.add(posOffsetVec);
      }

      if (bounds && bounds.centerX !== undefined && bounds.sizeX > 0) {
          const offsetWorld = new THREE.Vector3(bounds.centerX - obj.posX, bounds.centerY - obj.posY, -(bounds.centerZ - obj.posZ));
          // Apply inverse rotation
          const offsetLocal = offsetWorld.clone().applyQuaternion(group.quaternion.clone().invert());
          // Divide by scale because solidMesh is a child of group and gets scaled
          offsetLocal.divide(new THREE.Vector3(sx, sy, sz));
          
          solidMesh.position.copy(offsetLocal);
          wireframe.position.copy(offsetLocal);
      }

      if (obj.modelUrl) {
        const lowerUrl = obj.modelUrl.toLowerCase();

        const onLoadObject = (model: THREE.Object3D | { scene: THREE.Object3D }) => {
          // FBX, USDZ return a Group (Object3D), GLTFLoader returns an object with a 'scene' property
          const scene = 'scene' in model ? model.scene : model;

          // Normalize the loaded model pivot to its bounding box center
          const wrapper = new THREE.Group();
          scene.name = 'fbxRoot_' + obj.name;

          // Apply standard dashboard aesthetic (wireframe + transparent fill)
          const matColor = obj.isInteractable ? 0x22c55e : 0x475569;
          const fillOpacity = obj.isInteractable ? 0.25 : 0.08;
          const lineOpacity = obj.isInteractable ? 0.8 : 0.35;

          const customMat = new THREE.MeshBasicMaterial({
            color: matColor,
            transparent: true,
            opacity: fillOpacity,
            depthWrite: false,
            side: THREE.DoubleSide
          });

          scene.traverse((child: any) => {
            if (child.isMesh) {
              child.material = customMat;
              const edges = new THREE.EdgesGeometry(child.geometry);
              const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({
                color: matColor,
                transparent: true,
                opacity: lineOpacity
              }));
              child.add(line);
            }
          });

          // Unity's FBX Importer sometimes applies arbitrary coordinate system fixes and node rotations (like 0, -90, 90).
          // These rotations are baked into the FBX file, so FBXLoader applies them too.
          // To allow the user to easily align the model horizontally with a parent container, we strip ALL internal rotations from the FBX.
          scene.traverse((node: any) => {
            if (node.isObject3D && !node.isMesh) {
              node.rotation.set(0, 0, 0);
              node.position.set(0, 0, 0);
            }
          });
          scene.scale.set(1, 1, 1);

          // Rotation is now handled dynamically in applyFrame via rotationOffset

          wrapper.add(scene);
          group.add(wrapper);

          // Calculate Unity's intrinsic FBX Import Scale (e.g. 0.01 for cm -> m)
          // To calculate the pure uniform scale, we temporarily remove the group's non-uniform scale
          const tempScale = group.scale.clone();
          group.scale.set(1, 1, 1);
          group.updateMatrixWorld(true);

          const box = new THREE.Box3();
          wrapper.traverse((child: any) => {
            if (child.isMesh && child.geometry) {
              child.geometry.computeBoundingBox();
              if (child.geometry.boundingBox) {
                const childBox = child.geometry.boundingBox.clone();
                childBox.applyMatrix4(child.matrixWorld);
                box.union(childBox);
              }
            }
          });

          // Restore group's scale
          group.scale.copy(tempScale);

          if (!box.isEmpty()) {
            const bounds = obj.meshBounds || obj.colliderBounds;
            if (bounds) {
              const fbxSize = new THREE.Vector3();
              box.getSize(fbxSize);
              
              const fbxMax = Math.max(fbxSize.x, fbxSize.y, fbxSize.z);
              const unityMax = Math.max(bounds.sizeX, bounds.sizeY, bounds.sizeZ);

              if (fbxMax > 0 && unityMax > 0) {
                // targetScale is the pure uniform scale the FBX should have in world space
                const targetScale = unityMax / fbxMax;
                
                // We apply a scale to the wrapper that EXACTLY cancels out the group's (Parent's) non-uniform scale,
                // while applying the uniform targetScale. This prevents the FBX from being distorted by the Parent!
                wrapper.scale.set(
                  tempScale.x > 0 ? targetScale / tempScale.x : targetScale,
                  tempScale.y > 0 ? targetScale / tempScale.y : targetScale,
                  tempScale.z > 0 ? targetScale / tempScale.z : targetScale
                );
              }
            }
          }
        };

        const onModelDownloaded = () => {
          this.pendingModelDownloads$.next(this.pendingModelDownloads$.value - 1);
          if (this.pendingModelDownloads$.value === 0) {
      this.sceneReady$.next(true);
    }
        };

        const onError = (error: any) => {
          console.error(`Error loading model for ${obj.name}:`, error);
          group.add(solidMesh);
          group.add(wireframe);
          onModelDownloaded();
        };

        const successCallback = (model: any) => {
          onLoadObject(model);
          onModelDownloaded();
        };

        this.pendingModelDownloads$.next(this.pendingModelDownloads$.value + 1);

        if (lowerUrl.includes('.fbx') || lowerUrl.includes('.fbx?')) {
          const loader = new FBXLoader();
          loader.load(obj.modelUrl, successCallback, undefined, onError);
        } else if (lowerUrl.includes('.usdz') || lowerUrl.includes('.usdz?')) {
          const loader = new USDLoader();
          loader.load(obj.modelUrl, successCallback, undefined, onError);
        } else if (lowerUrl.includes('.ply') || lowerUrl.includes('.ply?')) {
          const loader = new PLYLoader();
          loader.load(obj.modelUrl, (geometry: any) => {
            geometry.computeVertexNormals();
            const material = new THREE.MeshStandardMaterial({
              color: color,
              flatShading: true,
              side: THREE.DoubleSide
            });
            const mesh = new THREE.Mesh(geometry, material);
            group.add(mesh);
            onModelDownloaded();
          }, undefined, onError);
        } else {
          // Fallback to GLTF loader for .glb, .gltf
          const loader = new GLTFLoader();
          loader.load(obj.modelUrl, successCallback, undefined, onError);
        }
      } else {
        // Fallback to generic mesh
        group.add(solidMesh);
        group.add(wireframe);
      }

      // We no longer set group.visible = false here because stationary dynamic objects might not send poses every frame

      this.sceneGroup.add(group);
    }

    if (this.pendingModelDownloads$.value === 0) {
      this.sceneReady$.next(true);
    }
  }

  // --- Camera & Selection Helpers ---
  private previousCameraPos: THREE.Vector3 | null = null;
  private previousCameraTarget: THREE.Vector3 | null = null;

  public focusCameraOnObject(name: string) {
    const targetChild = this.sceneGroup.children.find(c => c.name === name);
    if (targetChild && this.camera && this.controls) {
      if (!this.previousCameraPos) {
        this.previousCameraPos = this.camera.position.clone();
        this.previousCameraTarget = this.controls.target.clone();
      }

      const targetPos = new THREE.Vector3();
      targetChild.getWorldPosition(targetPos);
      this.controls.target.copy(targetPos);

      const offset = this.camera.position.clone().sub(this.controls.target).normalize().multiplyScalar(4);
      if (offset.lengthSq() < 0.001) offset.set(0, 0, 4);
      this.camera.position.copy(this.controls.target).add(offset);

      this.controls.update();
    }
  }

  public restoreCameraSelection() {
    if (this.previousCameraPos && this.previousCameraTarget && this.camera && this.controls) {
      this.camera.position.copy(this.previousCameraPos);
      this.controls.target.copy(this.previousCameraTarget);
      this.controls.update();
      this.previousCameraPos = null;
      this.previousCameraTarget = null;
    }
  }

  public resetCamera() {
    if (this.camera && this.controls) {
      this.camera.position.set(0, 2, 4);
      this.controls.target.set(0, 0, 0);
      this.controls.update();
    }
  }

}
